import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { MapPin, Plus, Edit, Power, Store } from 'lucide-react';
import { API, toast } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SearchBar from '@/components/shared/SearchBar';

const EMPTY_FORM = {
  name: '',
  country: '',
  city: '',
  district: '',
  full_address: '',
  phone: '',
  email: '',
  manager_name: '',
  opening_hours: '',
  is_active: true,
};

const normalizeCountries = (countries = []) =>
  (countries || []).map((country) => ({
    id: String(country?.code || '').trim().toUpperCase(),
    label: String(country?.name || country?.code || '').trim(),
    cities: (country?.cities || []).map((city) => ({
      id: String(city?.code || '').trim().toUpperCase(),
      label: String(city?.name || city?.code || '').trim(),
    })),
  }));

const RelayPointsManagement = () => {
  const [relayPayload, setRelayPayload] = useState({ items: [], summary: { total: 0, active: 0, inactive: 0 } });
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({ country: 'all', city: 'all', status: 'all' });
  const [showDialog, setShowDialog] = useState(false);
  const [editingRelayPoint, setEditingRelayPoint] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const normalizedCountries = useMemo(() => normalizeCountries(countries), [countries]);
  const availableCities = useMemo(() => {
    const selectedCountry = normalizedCountries.find((country) => country.id === formData.country);
    return selectedCountry?.cities || [];
  }, [formData.country, normalizedCountries]);

  const filterCities = useMemo(() => {
    const selectedCountry = normalizedCountries.find((country) => country.id === filters.country);
    return selectedCountry?.cities || [];
  }, [filters.country, normalizedCountries]);

  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return (relayPayload.items || []).filter((relayPoint) => {
      if (filters.country !== 'all' && relayPoint.country !== filters.country) {
        return false;
      }
      if (filters.city !== 'all' && relayPoint.city !== filters.city) {
        return false;
      }
      if (filters.status === 'active' && !relayPoint.is_active) {
        return false;
      }
      if (filters.status === 'inactive' && relayPoint.is_active) {
        return false;
      }
      if (!query) {
        return true;
      }
      const haystack = [
        relayPoint.name,
        relayPoint.code,
        relayPoint.city_name,
        relayPoint.country_name,
        relayPoint.district,
        relayPoint.manager_name,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [filters.city, filters.country, filters.status, relayPayload.items, searchQuery]);

  const loadRelayPoints = async () => {
    const params = {};
    if (filters.country !== 'all') {
      params.country = filters.country;
    }
    if (filters.city !== 'all') {
      params.city = filters.city;
    }
    if (filters.status === 'active') {
      params.is_active = true;
    }
    if (filters.status === 'inactive') {
      params.is_active = false;
    }
    const response = await axios.get(`${API}/admin/relay-points`, { params });
    setRelayPayload(response.data || { items: [], summary: { total: 0, active: 0, inactive: 0 } });
  };

  const loadCountries = async () => {
    const response = await axios.get(`${API}/countries`);
    setCountries(response.data || []);
  };

  const loadAll = async () => {
    try {
      setLoading(true);
      await Promise.all([loadRelayPoints(), loadCountries()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de charger les points relais.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    loadRelayPoints().catch((error) => {
      toast.error(error.response?.data?.detail || 'Impossible de filtrer les points relais.');
    });
  }, [filters.country, filters.city, filters.status]);

  const handleCreate = () => {
    setEditingRelayPoint(null);
    setFormData(EMPTY_FORM);
    setShowDialog(true);
  };

  const handleEdit = (relayPoint) => {
    setEditingRelayPoint(relayPoint);
    setFormData({
      name: relayPoint.name || '',
      country: relayPoint.country || '',
      city: relayPoint.city || relayPoint.city_code || '',
      district: relayPoint.district || '',
      full_address: relayPoint.full_address || relayPoint.address || '',
      phone: relayPoint.phone || '',
      email: relayPoint.email || '',
      manager_name: relayPoint.manager_name || '',
      opening_hours: relayPoint.opening_hours || '',
      is_active: Boolean(relayPoint.is_active),
    });
    setShowDialog(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      if (editingRelayPoint) {
        await axios.patch(`${API}/admin/relay-points/${editingRelayPoint.id}`, formData);
        toast.success('Point relais mis a jour.');
      } else {
        await axios.post(`${API}/admin/relay-points`, formData);
        toast.success('Point relais cree.');
      }
      setShowDialog(false);
      setEditingRelayPoint(null);
      setFormData(EMPTY_FORM);
      await loadRelayPoints();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible d’enregistrer ce point relais.');
    }
  };

  const handleToggleActive = async (relayPoint) => {
    try {
      await axios.patch(`${API}/admin/relay-points/${relayPoint.id}`, {
        is_active: !relayPoint.is_active,
      });
      toast.success(relayPoint.is_active ? 'Point relais desactive.' : 'Point relais reactive.');
      await loadRelayPoints();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de mettre a jour le statut.');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center p-12"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Store className="h-6 w-6 text-cyan-600" />
                Points relais
              </CardTitle>
              <CardDescription>
                Gere le reseau physique Coli Noki par pays, ville et statut d’activation.
              </CardDescription>
            </div>
            <Button
              data-testid="create-relay-point-btn"
              className="bg-cyan-500 hover:bg-cyan-600"
              onClick={handleCreate}
            >
              <Plus className="mr-2 h-4 w-4" />
              Nouveau point relais
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <Card className="border-gray-200">
              <CardHeader className="pb-2">
                <CardDescription>Total</CardDescription>
                <CardTitle>{relayPayload.summary?.total || 0}</CardTitle>
              </CardHeader>
            </Card>
            <Card className="border-green-100 bg-green-50">
              <CardHeader className="pb-2">
                <CardDescription>Actifs</CardDescription>
                <CardTitle className="text-green-700">{relayPayload.summary?.active || 0}</CardTitle>
              </CardHeader>
            </Card>
            <Card className="border-red-100 bg-red-50">
              <CardHeader className="pb-2">
                <CardDescription>Desactives</CardDescription>
                <CardTitle className="text-red-700">{relayPayload.summary?.inactive || 0}</CardTitle>
              </CardHeader>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-[1.4fr,1fr,1fr,1fr]">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Rechercher un point relais, une ville ou un code..."
            />
            <select
              data-testid="relay-filter-country"
              className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.country}
              onChange={(event) => setFilters((current) => ({ ...current, country: event.target.value, city: 'all' }))}
            >
              <option value="all">Tous les pays</option>
              {normalizedCountries.map((country) => (
                <option key={country.id} value={country.id}>{country.label}</option>
              ))}
            </select>
            <select
              data-testid="relay-filter-city"
              className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.city}
              onChange={(event) => setFilters((current) => ({ ...current, city: event.target.value }))}
            >
              <option value="all">Toutes les villes</option>
              {filterCities.map((city) => (
                <option key={city.id} value={city.id}>{city.label}</option>
              ))}
            </select>
            <select
              data-testid="relay-filter-status"
              className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.status}
              onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="inactive">Desactives</option>
            </select>
          </div>

          {filteredItems.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-200 px-6 py-12 text-center text-gray-500">
              Aucun point relais ne correspond aux filtres.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Point relais</TableHead>
                  <TableHead>Localisation</TableHead>
                  <TableHead>Responsable</TableHead>
                  <TableHead>Horaires</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((relayPoint) => (
                  <TableRow key={relayPoint.id}>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="font-medium">{relayPoint.name}</div>
                        <div className="text-xs text-gray-500">{relayPoint.code}</div>
                        <div className="text-sm text-gray-500">{relayPoint.full_address}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-gray-900">{relayPoint.city_name || relayPoint.city}</div>
                        <div className="text-xs text-gray-500">{relayPoint.country_name || relayPoint.country}</div>
                        {relayPoint.district && <div className="text-xs text-gray-500">{relayPoint.district}</div>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="text-sm text-gray-900">{relayPoint.manager_name || 'Non renseigne'}</div>
                        {relayPoint.phone && <div className="text-xs text-gray-500">{relayPoint.phone}</div>}
                        {relayPoint.email && <div className="text-xs text-gray-500">{relayPoint.email}</div>}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">{relayPoint.opening_hours || '-'}</TableCell>
                    <TableCell>
                      {relayPoint.is_active ? (
                        <Badge className="bg-green-100 text-green-700">Actif</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700">Desactive</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`edit-relay-point-${relayPoint.id}`}
                          onClick={() => handleEdit(relayPoint)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`toggle-relay-point-${relayPoint.id}`}
                          onClick={() => handleToggleActive(relayPoint)}
                        >
                          <Power className={`h-4 w-4 ${relayPoint.is_active ? 'text-orange-600' : 'text-green-600'}`} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editingRelayPoint ? 'Modifier le point relais' : 'Nouveau point relais'}</DialogTitle>
            <DialogDescription>
              Structure reutilisable pour l’admin aujourd’hui, puis pour le public et les choix logistiques plus tard.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="relay-point-name">Nom *</Label>
                <Input
                  id="relay-point-name"
                  data-testid="relay-point-name"
                  value={formData.name}
                  onChange={(event) => setFormData((current) => ({ ...current, name: event.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="relay-point-manager">Responsable</Label>
                <Input
                  id="relay-point-manager"
                  value={formData.manager_name}
                  onChange={(event) => setFormData((current) => ({ ...current, manager_name: event.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="relay-point-country">Pays *</Label>
                <select
                  id="relay-point-country"
                  data-testid="relay-point-country"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.country}
                  onChange={(event) => setFormData((current) => ({ ...current, country: event.target.value, city: '' }))}
                  required
                >
                  <option value="">Selectionner un pays</option>
                  {normalizedCountries.map((country) => (
                    <option key={country.id} value={country.id}>{country.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="relay-point-city">Ville *</Label>
                <select
                  id="relay-point-city"
                  data-testid="relay-point-city"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.city}
                  onChange={(event) => setFormData((current) => ({ ...current, city: event.target.value }))}
                  required
                >
                  <option value="">Selectionner une ville</option>
                  {availableCities.map((city) => (
                    <option key={city.id} value={city.id}>{city.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="relay-point-district">Quartier</Label>
                <Input
                  id="relay-point-district"
                  value={formData.district}
                  onChange={(event) => setFormData((current) => ({ ...current, district: event.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="relay-point-phone">Telephone</Label>
                <Input
                  id="relay-point-phone"
                  value={formData.phone}
                  onChange={(event) => setFormData((current) => ({ ...current, phone: event.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="relay-point-email">Email</Label>
                <Input
                  id="relay-point-email"
                  type="email"
                  value={formData.email}
                  onChange={(event) => setFormData((current) => ({ ...current, email: event.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="relay-point-opening-hours">Horaires</Label>
                <Input
                  id="relay-point-opening-hours"
                  value={formData.opening_hours}
                  onChange={(event) => setFormData((current) => ({ ...current, opening_hours: event.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="relay-point-address">Adresse complete *</Label>
              <Input
                id="relay-point-address"
                data-testid="relay-point-address"
                value={formData.full_address}
                onChange={(event) => setFormData((current) => ({ ...current, full_address: event.target.value }))}
                required
              />
            </div>

            <label className="flex items-center gap-3 rounded-lg border border-gray-200 p-3">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(event) => setFormData((current) => ({ ...current, is_active: event.target.checked }))}
              />
              <span className="text-sm text-gray-700">Point relais actif des sa creation</span>
            </label>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" data-testid="submit-relay-point" className="bg-cyan-500 hover:bg-cyan-600">
                {editingRelayPoint ? 'Enregistrer' : 'Creer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RelayPointsManagement;
